import { Dispatch } from 'react';
import { Action, Reducer } from 'redux';
import { PageResult } from '../../models/CoreModels';
import { CompareOneRosterUserEntraId, CoreOneRosterUser, OneRosterClass, OneRosterSchoolUnit } from '../../models/CoreOneRosterModels';
import { OneRosterClassSearchPageRequest, OneRosterUserSearchPageRequest, SearchPageRequest } from '../../models/Requests';
import { CacheRequestState, ExtendedRequestState, RequestState } from '../../models/RequestState';
import InstanceClient from '../../services/InstanceClient';
import StatusCode from '../../util/StatusCode';
import store from '../store';
import { RoleType } from '../../util/OneRoster/OneRosterRoleTypeUtil';

enum OneRosterUserActionType {
    ONEROSTER_USERS = 'ONEROSTER_USERS',
    ONEROSTER_USERS_FILTER = 'ONEROSTER_USERS_FILTER',
    ONEROSTER_USERS_UPDATE = 'ONEROSTER_USERS_UPDATE',
    ONEROSTER_USER = 'ONEROSTER_USER',
    ONEROSTER_USER_ORG = 'ONEROSTER_USER_ORG',
    ONEROSTER_USER_CLASS = 'ONEROSTER_USER_CLASS',
    ONEROSTER_USER_ROLES = 'ONEROSTER_USER_ROLES',
    ONEROSTER_USER_GET_INFO_FROM_ENTRAID = 'ONEROSTER_USER_GET_INFO_FROM_ENTRAID',
    ONEROSTER_USER_COMPARE_ENTRAID = 'ONEROSTER_USER_COMPARE_ENTRAID',
}

export type UserOrgRequestState = ExtendedRequestState<PageResult<OneRosterSchoolUnit>, { filter: Partial<SearchPageRequest> }>
export type UserClassRequestState = ExtendedRequestState<PageResult<OneRosterClass>, { filter: Partial<OneRosterClassSearchPageRequest> }>

export interface OneRosterUserState {
    users: CacheRequestState<PageResult<CoreOneRosterUser>>
    filter: Partial<OneRosterUserSearchPageRequest>
    user: RequestState<CoreOneRosterUser>
    roles: RoleType[]
    userSchool: UserOrgRequestState
    userClasses: UserClassRequestState
    infoFromEntraId: RequestState<string>
    compareOneRosterUser: RequestState<CompareOneRosterUserEntraId>
}

interface OneRosterUser_ListUsersBase extends Action<OneRosterUserActionType> {
    type: OneRosterUserActionType.ONEROSTER_USERS
    users: CacheRequestState<PageResult<CoreOneRosterUser>>
}

interface OneRosterUser_ListUsersFilter extends Action<OneRosterUserActionType> {
    type: OneRosterUserActionType.ONEROSTER_USERS_FILTER
    filter: Partial<OneRosterUserSearchPageRequest>
}

interface OneRosterUser_ListUsersUpdate extends Action<OneRosterUserActionType> {
    type: OneRosterUserActionType.ONEROSTER_USERS_UPDATE
}

type OneRosterUser_ListUsers = OneRosterUser_ListUsersBase
    | OneRosterUser_ListUsersFilter
    | OneRosterUser_ListUsersUpdate;

interface OneRosterUser_GetUser extends Action<OneRosterUserActionType> {
    type: OneRosterUserActionType.ONEROSTER_USER
    user: RequestState<CoreOneRosterUser>
}

interface OneRosterUser_GetRoles extends Action<OneRosterUserActionType> {
    type: OneRosterUserActionType.ONEROSTER_USER_ROLES
    roles: RoleType[]
}

interface OneRosterUser_GetOrgs extends Action<OneRosterUserActionType> {
    type: OneRosterUserActionType.ONEROSTER_USER_ORG
    userSchool: UserOrgRequestState
}

interface OneRosterUser_GetClasses extends Action<OneRosterUserActionType> {
    type: OneRosterUserActionType.ONEROSTER_USER_CLASS
    userClasses: UserClassRequestState
}

interface OneRosterUser_GetInfoFromEntra extends Action<OneRosterUserActionType> {
    type: OneRosterUserActionType.ONEROSTER_USER_GET_INFO_FROM_ENTRAID
    infoFromEntraId: RequestState<string>
}
interface OneRosterUser_CompareEntraId extends Action<OneRosterUserActionType> {
    type: OneRosterUserActionType.ONEROSTER_USER_COMPARE_ENTRAID
    compareOneRosterUser: RequestState<CompareOneRosterUserEntraId>
}


export const getOneRosterUsers = (filter: Partial<OneRosterUserSearchPageRequest> = {}, update = false) => (dispatch: Dispatch<OneRosterUser_ListUsers>) => {
    dispatch({ type: OneRosterUserActionType.ONEROSTER_USERS_FILTER, filter: filter });
    if (update) {
        dispatch({ type: OneRosterUserActionType.ONEROSTER_USERS_UPDATE });
    }

    const url = '/api/core/oneroster/user';
    InstanceClient.get<PageResult<CoreOneRosterUser>>(url, filter)
        .then(res => {
            dispatch({
                type: OneRosterUserActionType.ONEROSTER_USERS,
                users: {
                    code: StatusCode.COMPLETE,
                    data: res.data,
                },
            })
        })
        .catch(error =>
            dispatch({
                type: OneRosterUserActionType.ONEROSTER_USERS,
                users: { code: StatusCode.ERROR, error }
            })
        )
}

export const getOneRosterUser = (id: string) => (dispatch: Dispatch<OneRosterUser_GetUser>) => {
    const storeKey = store.getState().onerosterUser.user.key;
    const key = id;
    if (key === storeKey) {
        return;
    }

    const url = '/api/core/oneroster/user/' + id;
    InstanceClient.get<CoreOneRosterUser>(url)
        .then(res => {
            dispatch({
                type: OneRosterUserActionType.ONEROSTER_USER,
                user: { code: StatusCode.COMPLETE, data: res.data, key: key },
            })
        })
        .catch(error =>
            dispatch({
                type: OneRosterUserActionType.ONEROSTER_USER,
                user: { code: StatusCode.ERROR, error }
            })
        )
}

export const getOneRosterUserOrgs = (id: string, filter: Partial<SearchPageRequest> = {}) => (dispatch: Dispatch<OneRosterUser_GetOrgs>) => {
    const storeKey = store.getState().onerosterUser.userSchool.key;
    const key = id + '-' + filter.onlyActive + '-' + filter.page;
    if (key === storeKey) {
        return;
    }

    dispatch({
        type: OneRosterUserActionType.ONEROSTER_USER_ORG,
        userSchool: { code: StatusCode.PENDING, filter },
    })

    const url = `/api/core/oneroster/user/${id}/school`;
    InstanceClient.get<PageResult<OneRosterSchoolUnit>>(url, filter)
        .then(res => {
            dispatch({
                type: OneRosterUserActionType.ONEROSTER_USER_ORG,
                userSchool: { code: StatusCode.COMPLETE, data: res.data, key, filter },
            })
        })
        .catch(error =>
            dispatch({
                type: OneRosterUserActionType.ONEROSTER_USER_ORG,
                userSchool: { code: StatusCode.ERROR, error, filter }
            })
        )
}

export const getOneRosterUserClasses = (id: string, filter: Partial<OneRosterClassSearchPageRequest> = {}) => (dispatch: Dispatch<OneRosterUser_GetClasses>) => {
    const storeKey = store.getState().onerosterUser.userClasses.key;
    const key = id + '-' + filter.status + '-' + filter.page;
    if (key === storeKey) {
        return;
    }

    dispatch({
        type: OneRosterUserActionType.ONEROSTER_USER_CLASS,
        userClasses: { code: StatusCode.PENDING, filter },
    })

    const url = `/api/core/oneroster/user/${id}/class`;
    InstanceClient.get<PageResult<OneRosterClass>>(url, filter)
        .then(res => {
            dispatch({
                type: OneRosterUserActionType.ONEROSTER_USER_CLASS,
                userClasses: { code: StatusCode.COMPLETE, data: res.data, key, filter },
            })
        })
        .catch(error =>
            dispatch({
                type: OneRosterUserActionType.ONEROSTER_USER_CLASS,
                userClasses: { code: StatusCode.ERROR, error, filter }
            })
        )
}

export const getOneRosterRoles = () => (dispatch: Dispatch<OneRosterUser_GetRoles>) => {
    const url = '/api/core/oneroster/user/roles';
    InstanceClient.get<RoleType[]>(url)
        .then(res => {
            dispatch({
                type: OneRosterUserActionType.ONEROSTER_USER_ROLES,
                roles: res.data,
            })
        })
}

export const GetOneRosterUserInfoFromEntra = (sisId: string) => (dispatch: Dispatch<OneRosterUser_GetInfoFromEntra>) => {
    dispatch({
        type: OneRosterUserActionType.ONEROSTER_USER_GET_INFO_FROM_ENTRAID,
        infoFromEntraId: { code: StatusCode.PENDING }
    })
    const url = `/api/core/oneroster/user/tool/${sisId}`;
    InstanceClient.get<any>(url)
        .then(res => 
            dispatch({
                type: OneRosterUserActionType.ONEROSTER_USER_GET_INFO_FROM_ENTRAID,
                infoFromEntraId: { code: StatusCode.COMPLETE, data: JSON.stringify(res.data) }
            }))
        .catch(error =>
            dispatch({
                type: OneRosterUserActionType.ONEROSTER_USER_GET_INFO_FROM_ENTRAID,
                infoFromEntraId: { code: StatusCode.ERROR, error }
            }))
}

export const GetOneRosterUserCompareFromEntra = (sisId: string) => (dispatch: Dispatch<OneRosterUser_CompareEntraId>) => {
    dispatch({
        type: OneRosterUserActionType.ONEROSTER_USER_COMPARE_ENTRAID,
        compareOneRosterUser: { code: StatusCode.PENDING }
    })
    const url = `/api/core/oneroster/user/tool/compare/${sisId}`;
    InstanceClient.get<CompareOneRosterUserEntraId>(url)
        .then(res => 
            dispatch({
                type: OneRosterUserActionType.ONEROSTER_USER_COMPARE_ENTRAID,
                compareOneRosterUser: { code: StatusCode.COMPLETE, data: res.data }
            }))
        .catch(error =>
            dispatch({
                type: OneRosterUserActionType.ONEROSTER_USER_COMPARE_ENTRAID,
                compareOneRosterUser: { code: StatusCode.ERROR, error }
            }))
}

type OneRosterUserAction = OneRosterUser_ListUsers
    | OneRosterUser_GetUser
    | OneRosterUser_GetRoles
    | OneRosterUser_GetClasses
    | OneRosterUser_GetOrgs
    | OneRosterUser_GetInfoFromEntra
    | OneRosterUser_CompareEntraId;


const initialState: OneRosterUserState = {
    users: { code: StatusCode.PENDING },
    user: { code: StatusCode.PENDING },
    filter: {},
    userSchool: { code: StatusCode.PENDING, filter: {} },
    userClasses: { code: StatusCode.PENDING, filter: {} },
    roles: [],
    infoFromEntraId: { code: StatusCode.NONE },
    compareOneRosterUser: { code: StatusCode.NONE },
}

export const reducer: Reducer<OneRosterUserState, OneRosterUserAction> = (state, action) => {
    state = state || initialState;

    switch (action.type) {
        case OneRosterUserActionType.ONEROSTER_USERS:
            return {
                ...state,
                users: action.users,
            }
        case OneRosterUserActionType.ONEROSTER_USERS_FILTER:
            return {
                ...state,
                filter: action.filter
            }
        case OneRosterUserActionType.ONEROSTER_USERS_UPDATE:
            return {
                ...state,
                users: { ...state.users, code: StatusCode.PENDING }
            }
        case OneRosterUserActionType.ONEROSTER_USER:
            return {
                ...state,
                user: action.user
            }
        case OneRosterUserActionType.ONEROSTER_USER_ORG:
            return {
                ...state,
                userSchool: action.userSchool,
            }
        case OneRosterUserActionType.ONEROSTER_USER_CLASS:
            return {
                ...state,
                userClasses: action.userClasses,
            }
        case OneRosterUserActionType.ONEROSTER_USER_ROLES:
            return {
                ...state,
                roles: action.roles,
            }
        case OneRosterUserActionType.ONEROSTER_USER_GET_INFO_FROM_ENTRAID:
            return {
                ...state,
                infoFromEntraId: action.infoFromEntraId,
            };
        case OneRosterUserActionType.ONEROSTER_USER_COMPARE_ENTRAID:
            return {
                ...state,
                compareOneRosterUser: action.compareOneRosterUser,
            };
        default:
            return state;
    }
}